import { z } from 'zod';
export { deviceClassificationRules, deviceClassificationOptions, normalizeDeviceClassification, deviceClassificationErrors } from './device-classification.js';
export const entityStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'ARCHIVED']);
export const entityTypeSchema = z.enum(['COMPANY', 'CONTACT', 'SITE', 'MEMBERSHIP', 'DEVICE']);
export const actorTypeSchema = z.enum(['USER', 'SERVICE', 'SYSTEM']);
export const integrationStatusSchema = z.enum(['ACTIVE', 'DISABLED', 'ERROR']);
export const provisioningStatusSchema = z.enum(['NOT_CONFIGURED', 'PENDING', 'READY', 'FAILED']);
const optionalText = z.string().trim().max(500).optional().nullable();
export const pageQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const deviceCategorySchema = z.enum(['CLIENT', 'SERVER', 'MOBILE', 'NETWORK', 'PRINTER', 'OTHER']);
export const deviceTypeSchema = z.enum(['UNKNOWN', 'FIREWALL', 'ROUTER', 'SWITCH', 'ACCESS_POINT', 'NETWORK_CONTROLLER', 'UPS', 'NAS', 'PBX', 'NOTEBOOK', 'WORKSTATION', 'SERVER', 'MOBILE', 'PRINTER', 'OTHER']);
export const deviceExecutionSchema = z.enum(['UNKNOWN', 'PHYSICAL', 'VIRTUAL']);
export const deviceHostingSchema = z.enum(['UNKNOWN', 'ON_PREMISES', 'CLOUD', 'HOSTED']);
export const deviceFormFactorSchema = z.enum(['UNKNOWN', 'MINI_PC', 'DESKTOP', 'TOWER', 'RACK', 'APPLIANCE']);
export const deviceRoleSchema = z.enum(['VIRTUALIZATION_HOST']);
const deviceReference = z.string().trim().min(1).max(64).nullable().optional();
const deviceText = z.string().trim().min(1).max(200).nullable().optional();
export const deviceCreateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    category: deviceCategorySchema.default('OTHER'),
    deviceType: deviceTypeSchema.default('UNKNOWN'),
    execution: deviceExecutionSchema.default('UNKNOWN'),
    hosting: deviceHostingSchema.default('ON_PREMISES'),
    formFactor: deviceFormFactorSchema.default('UNKNOWN'),
    roles: z.array(deviceRoleSchema).max(1).default([]),
    hostingProvider: deviceText,
    hostingRegion: deviceText,
    hostname: deviceText,
    manufacturer: deviceText,
    model: deviceText,
    serialNumber: deviceText,
    assetTag: deviceText,
    companyId: deviceReference,
    siteId: deviceReference,
    responsibleContactId: deviceReference,
    status: entityStatusSchema.exclude(['ARCHIVED']).default('ACTIVE'),
}).strict();
export const devicePatchSchema = deviceCreateSchema.partial().extend({
    category: deviceCategorySchema.optional(), status: entityStatusSchema.exclude(['ARCHIVED']).optional(),
    deviceType: deviceTypeSchema.optional(), execution: deviceExecutionSchema.optional(),
    hosting: deviceHostingSchema.optional(), formFactor: deviceFormFactorSchema.optional(),
    roles: z.array(deviceRoleSchema).max(1).optional(),
}).strict();
export const deviceQuerySchema = pageQuerySchema.extend({
    search: z.string().trim().max(200).optional(),
    category: deviceCategorySchema.optional(),
    deviceType: deviceTypeSchema.optional(),
    execution: deviceExecutionSchema.optional(),
    hosting: deviceHostingSchema.optional(),
    status: entityStatusSchema.optional(),
    companyId: z.string().min(1).max(64).optional(),
    siteId: z.string().min(1).max(64).optional(),
    unassigned: z.enum(['true', 'false']).transform((value) => value === 'true').optional(),
    integrationInstanceId: z.string().min(1).max(64).optional(),
    externalType: z.string().trim().min(1).max(100).toUpperCase().optional(),
    externalId: z.string().trim().min(1).max(500).optional(),
}).superRefine((query, context) => {
    if (query.unassigned && (query.companyId || query.siteId))
        context.addIssue({ code: 'custom', message: 'Unzugeordnete Geräte können nicht nach Firma oder Standort gefiltert werden.' });
    const external = [query.integrationInstanceId, query.externalType, query.externalId];
    if (external.some(Boolean) && !external.every(Boolean))
        context.addIssue({ code: 'custom', message: 'Externe Suche erfordert Integrationsinstanz, Typ und ID.' });
});
export const deviceSchema = deviceCreateSchema.extend({
    id: z.string(),
    hardwareUuid: z.string().nullable().optional(),
    mergedIntoId: z.string().nullable().optional(),
    category: deviceCategorySchema,
    status: entityStatusSchema,
    hostname: z.string().nullable(), manufacturer: z.string().nullable(), model: z.string().nullable(),
    serialNumber: z.string().nullable(), assetTag: z.string().nullable(),
    companyId: z.string().nullable(), siteId: z.string().nullable(), responsibleContactId: z.string().nullable(),
    revision: z.number().int().positive(),
    createdAt: z.iso.datetime(), updatedAt: z.iso.datetime(), archivedAt: z.iso.datetime().nullable(),
    createdBy: z.string(), updatedBy: z.string(),
    company: z.object({ id: z.string(), name: z.string() }).nullable(),
    site: z.object({ id: z.string(), name: z.string() }).nullable(),
    responsibleContact: z.object({ id: z.string(), displayName: z.string() }).nullable(),
    deviceDesk: z.array(z.object({
        integrationInstanceId: z.string(),
        accessStatus: z.enum(['ACTIVE', 'REVOKED', 'UNKNOWN']),
        revokedAt: z.iso.datetime().nullable(),
        reportedAt: z.iso.datetime().nullable(),
    })).optional(),
});
export const deviceListSchema = z.object({ items: z.array(deviceSchema), page: z.number(), pageSize: z.number(), total: z.number() });
export const deviceRegistrationSchema = z.object({
    externalId: z.uuid(),
    companyId: z.string().trim().min(1).max(64),
    hostname: z.string().trim().max(200),
    hardwareUuid: z.string().trim().max(64).optional(),
    manufacturer: z.string().trim().max(200).optional(),
    serialNumber: z.string().trim().max(200).optional(),
    category: deviceCategorySchema.optional(),
    deviceType: deviceTypeSchema.optional(),
    execution: deviceExecutionSchema.optional(),
    formFactor: deviceFormFactorSchema.optional(),
    roles: z.array(deviceRoleSchema).max(1).optional(),
}).strict();
export const deviceLifecycleActionSchema = z.enum(['UNLINK', 'ARCHIVE', 'RESTORE', 'TRANSFER', 'MERGE']);
export const deviceLifecycleSchema = z.object({
    action: deviceLifecycleActionSchema,
    integrationInstanceIds: z.array(z.string().min(1).max(64)).max(30).default([]),
    targetCompanyId: z.string().min(1).max(64).optional(),
    targetDeviceId: z.string().min(1).max(64).optional(),
    previewHash: z.string().regex(/^[a-f0-9]{64}$/).optional(),
}).strict().superRefine((input, ctx) => {
    if (input.action === 'UNLINK' && !input.integrationInstanceIds.length)
        ctx.addIssue({ code: 'custom', message: 'Mindestens eine App auswählen.' });
    if ((input.action === 'TRANSFER') !== Boolean(input.targetCompanyId))
        ctx.addIssue({ code: 'custom', message: 'Kundenwechsel erfordert genau einen Zielkunden.' });
    if ((input.action === 'MERGE') !== Boolean(input.targetDeviceId))
        ctx.addIssue({ code: 'custom', message: 'Zusammenführen erfordert genau ein Zielgerät.' });
    if (input.action !== 'UNLINK' && input.integrationInstanceIds.length)
        ctx.addIssue({ code: 'custom', message: 'Diese Aktion betrifft alle Apps.' });
});
export const deviceLifecycleReportSchema = z.object({
    status: z.enum(['RUNNING', 'SUCCEEDED', 'FAILED', 'MANUAL_REQUIRED']),
    generation: z.number().int().positive(),
    accessRevoked: z.boolean(),
    collectionStopped: z.boolean(),
    sessionsClosed: z.boolean().nullable(),
    errorCode: z.string().regex(/^[A-Z0-9_]{1,64}$/).nullable().default(null),
}).strict();
export const deviceRegistrationResolutionSchema = z.object({
    action: z.enum(['LINK', 'CREATE_NEW', 'REJECT']),
    targetDeviceId: z.string().min(1).max(64).optional(),
    targetRevision: z.number().int().positive().optional(),
}).strict().superRefine((input, ctx) => {
    if ((input.action === 'LINK') !== Boolean(input.targetDeviceId && input.targetRevision))
        ctx.addIssue({ code: 'custom', message: 'Vorhandenes Gerät und aktuelle Revision auswählen.' });
});
export const deviceResumeSchema = z.object({ companyId: z.string().min(1).max(64), generation: z.number().int().positive(), activationId: z.uuid() }).strict();
export const deviceDeskAttributesSchema = devicePatchSchema.pick({
    hostname: true, manufacturer: true, model: true, serialNumber: true,
    deviceType: true, execution: true, formFactor: true, roles: true,
}).strict();
export const deviceDeskReportSchema = z.object({
    bindingGeneration: z.number().int().positive().optional(),
    sourceVersion: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    revokedAt: z.iso.datetime().nullable(),
    attributes: deviceDeskAttributesSchema,
    hardwareUuid: z.string().trim().max(64).optional(),
    removedAt: z.iso.datetime().nullable().optional(),
}).strict();
export const companyCreateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    legalName: optionalText,
    notes: z.string().trim().max(10_000).optional().nullable(),
    status: entityStatusSchema.exclude(['ARCHIVED']).default('ACTIVE'),
    domains: z.array(z.object({ domain: z.string().trim().min(1).max(253), isPrimary: z.boolean().default(false) })).max(50).default([]),
});
export const companyPatchSchema = companyCreateSchema.omit({ domains: true }).partial().extend({ revision: z.number().int().min(1).optional() });
export const contactIdentitySchema = z.object({
    type: z.enum(['EMAIL', 'PHONE', 'MOBILE']),
    value: z.string().trim().min(1).max(320),
    label: z.string().trim().max(100).optional().nullable(),
    isPrimary: z.boolean().default(false),
    origin: z.string().trim().min(1).max(100).default('CONTROL'),
});
export const contactCreateSchema = z.object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    displayName: z.string().trim().min(1).max(220).optional(),
    jobTitle: z.string().trim().max(200).optional().nullable(),
    status: entityStatusSchema.exclude(['ARCHIVED']).default('ACTIVE'),
    identities: z.array(contactIdentitySchema).max(20).default([]),
});
export const contactPatchSchema = contactCreateSchema.omit({ identities: true }).partial().extend({
    companyId: z.string().min(1).max(100).nullable().optional(),
    revision: z.number().int().min(1).optional(),
});
export const siteCreateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    street: optionalText,
    postalCode: optionalText,
    city: optionalText,
    state: optionalText,
    countryCode: z.string().trim().length(2).toUpperCase().optional().nullable(),
    timezone: z.string().trim().max(100).optional().nullable(),
    isPrimary: z.boolean().default(false),
    status: entityStatusSchema.exclude(['ARCHIVED']).default('ACTIVE'),
});
export const sitePatchSchema = siteCreateSchema.partial().extend({ revision: z.number().int().min(1).optional() });
export const externalIdentityCreateSchema = z.object({
    entityType: entityTypeSchema,
    entityId: z.string().min(1).max(100),
    provider: z.string().trim().min(1).max(100).toUpperCase(),
    integrationInstanceId: z.string().min(1).max(100).optional().nullable(),
    externalType: z.string().trim().min(1).max(100).toUpperCase(),
    externalId: z.string().trim().min(1).max(500),
    metadata: z.record(z.string(), z.unknown()).optional().nullable(),
});
export const contactIdentityCreateSchema = contactIdentitySchema;
export const contactIdentityPatchSchema = contactIdentitySchema.partial().extend({ revision: z.number().int().positive() });
export const membershipCreateSchema = z.object({
    companyId: z.string().min(1).max(100),
    contactId: z.string().min(1).max(100),
    position: z.string().trim().max(200).optional().nullable(),
    status: z.enum(['ACTIVE', 'INACTIVE', 'ENDED']).default('ACTIVE'),
    validFrom: z.iso.datetime().optional().nullable(),
    validUntil: z.iso.datetime().optional().nullable(),
});
export const membershipPatchSchema = membershipCreateSchema.omit({ companyId: true, contactId: true }).partial().extend({ revision: z.number().int().positive() });
export const integrationCreateSchema = z.object({
    key: z.string().regex(/^[a-z0-9-]+$/).max(100),
    name: z.string().trim().min(1).max(200),
    type: z.enum(['INTERNAL', 'EXTERNAL']),
    status: integrationStatusSchema.default('ACTIVE'),
    capabilities: z.array(z.string().min(1).max(200)).max(100).default([]),
});
export const integrationInstanceCreateSchema = z.object({
    integrationId: z.string().min(1).max(100),
    key: z.string().regex(/^[a-z0-9-]+$/).max(150),
    name: z.string().trim().min(1).max(200),
    environment: z.string().trim().min(1).max(100),
    status: integrationStatusSchema.default('ACTIVE'),
    configuration: z.record(z.string(), z.unknown()).default({}),
    secretRef: z.string().trim().max(500).optional().nullable(),
});
export const contactReconcileSchema = z.object({ integrationInstanceId: z.string().min(1).max(100), assignmentVersion: z.number().int().positive() }).strict();
export const assignmentUpsertSchema = z.object({
    entityType: entityTypeSchema,
    entityId: z.string().min(1).max(100),
    integrationInstanceId: z.string().min(1).max(100),
    status: z.enum(['ENABLED', 'DISABLED']),
    expectedVersion: z.number().int().positive().optional(),
});
export const integrationCompanyDirectorySchema = z.object({
    items: z.array(z.object({
        company: z.object({ id: z.string(), name: z.string(), status: entityStatusSchema, revision: z.number().int().positive() }),
        assignment: z.object({
            id: z.string(), status: z.enum(['ENABLED', 'DISABLED']), version: z.number().int().positive(),
            reportVersion: z.number().int().nonnegative(), provisioningStatus: provisioningStatusSchema,
            appliedEntityRevision: z.number().int().nullable(), appliedAssignmentVersion: z.number().int().nullable(),
            externalIdentityId: z.string().nullable(), needsRetry: z.boolean(),
        }),
    })),
    page: z.number().int().positive(), pageSize: z.number().int().min(1).max(100), total: z.number().int().nonnegative(),
});
export const provisioningReportSchema = z.object({
    assignmentId: z.string().min(1).max(100),
    integrationInstanceId: z.string().min(1).max(100),
    assignmentVersion: z.number().int().positive(),
    appliedEntityRevision: z.number().int().nonnegative(),
    reportVersion: z.number().int().positive(),
    status: provisioningStatusSchema,
    externalIdentityId: z.string().max(100).optional().nullable(),
    errorCode: z.string().max(100).optional().nullable(),
    errorMessage: z.string().max(2000).optional().nullable(),
    needsRetry: z.boolean().default(false),
});
export const lifecycleRequestSchema = z.object({
    type: z.enum(['DEACTIVATE', 'SECURITY_BLOCK', 'ARCHIVE', 'RESTORE', 'MERGE', 'UNLINK', 'DELETE_OR_ANONYMIZE']),
    entityType: entityTypeSchema,
    entityId: z.string().min(1).max(100),
    policyKey: z.string().max(200).optional().nullable(),
    targetEntityId: z.string().max(100).optional(),
    integrationInstanceIds: z.array(z.string().min(1).max(100)).max(100).default([]),
    execute: z.boolean().default(false),
});
export const lifecycleReportSchema = z.object({
    integrationInstanceId: z.string().min(1).max(100),
    requestedVersion: z.number().int().positive(),
    status: z.enum(['RUNNING', 'PARTIAL', 'SUCCEEDED', 'FAILED', 'MANUAL_REQUIRED']),
    result: z.record(z.string(), z.unknown()).optional().nullable(),
    errorCode: z.string().max(100).optional().nullable(),
    errorMessage: z.string().max(2000).optional().nullable(),
});
export const provisioningUpsertSchema = z.object({
    entityType: entityTypeSchema,
    entityId: z.string().min(1).max(100),
    integrationId: z.string().min(1).max(100),
    status: provisioningStatusSchema,
    externalIdentityId: z.string().optional().nullable(),
    errorCode: z.string().max(100).optional().nullable(),
    errorMessage: z.string().max(2000).optional().nullable(),
});
export const webhookSubscriptionCreateSchema = z.object({
    integrationId: z.string().min(1),
    url: z.url().max(2000),
    secret: z.string().min(32).max(500),
    eventTypes: z.array(z.string().min(1).max(200)).min(1).max(100),
    active: z.boolean().default(true),
});
//# sourceMappingURL=index.js.map